// external libraries
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';

// types
import type { Interval } from 'components/types';

// utils
import EventBus from 'lib/EventBusUpdated';
import { createCoalescedInvalidator } from '../utils/createCoalescedInvalidator';
import { createReconnectCatchUp } from '../utils/createReconnectCatchUp';
import { getRosterLiveUpdateSubscriptions } from '../utils/getRosterLiveUpdateSubscriptions';

export type UseRosterPlanLiveUpdatesProps = Interval & {
  /** False while the grids are not on screen or not allowed; nothing is subscribed then. */
  enabled: boolean;
};

/**
 * Keeps the grids in step with changes made elsewhere — another tab, another planner, a
 * calculation — by refetching on an eventbus message. It only invalidates; the message is never
 * applied to the cache itself (#2498, architecture decision 4).
 *
 * Messages are coalesced per burst (`utils/createCoalescedInvalidator.ts`), so a bulk action
 * elsewhere costs one refetch per grid rather than one per changed duty line.
 *
 * The subscriptions follow the interval, as the addresses are per month. They do not follow the
 * plan units: no address carries one, and the invalidation goes by query key prefix, so it reaches
 * whichever plan units are on screen when the burst ends. Unsubscribing on unmount cancels a
 * pending refetch as well, so nothing fires for a screen that is gone. After a reconnect the grids
 * are refetched once, for the messages the dropped socket missed — for a hidden tab only once it is
 * shown again (`utils/createReconnectCatchUp.ts`).
 */
const useRosterPlanLiveUpdates = ({
  startDate,
  endDate,
  enabled
}: UseRosterPlanLiveUpdatesProps) => {
  const queryClient = useQueryClient();

  // The dates are rebuilt on every period change; their time keeps the effect off object identity.
  const startTime = startDate.getTime();
  const endTime = endDate.getTime();

  useEffect(() => {
    if (!enabled) return;

    const invalidator = createCoalescedInvalidator(queryClient);

    const subscriptions = getRosterLiveUpdateSubscriptions({
      startDate: new Date(startTime),
      endDate: new Date(endTime)
    });
    const registrations = subscriptions.map(
      ({ address, queryKey, createFilter }) => {
        const refetch = () => invalidator.add(queryKey);
        const filter = createFilter?.(queryClient, refetch);
        return {
          address,
          filter,
          // The bridge hands an error frame over as the first argument, with no message: it says
          // nothing about the roster, so it refreshes nothing.
          handler: (error: unknown, message?: unknown) => {
            if (error) return;
            if (filter) filter.handle(message);
            else refetch();
          }
        };
      }
    );

    registrations.forEach(({ address, handler }) =>
      EventBus.register(address, handler)
    );
    const stopCatchingUp = createReconnectCatchUp(() =>
      subscriptions.forEach(({ queryKey }) => invalidator.add(queryKey))
    );

    return () => {
      stopCatchingUp();
      invalidator.cancel();
      registrations.forEach(({ address, handler, filter }) => {
        EventBus.unregister(address, handler);
        filter?.dispose();
      });
    };
  }, [enabled, startTime, endTime, queryClient]);
};

export default useRosterPlanLiveUpdates;
